import React, { useState, useEffect } from "react";
import { Typography } from "@material-ui/core";
import ActionButton from "../../components/ActionButton";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import PropertyNames from "../../PropertyNames";
import { useSelector, useDispatch } from "react-redux";
import { setApplicationProperties } from "../../redux/actions/universalActions.js";
import { attachmentClient, taxServiceClient } from "../../AxiosClient";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import defaultLogo from "../../images/default-logo.svg";
import spinner from "../../images/loading.gif";
import UploadError from "../../components/messages/UploadError";
const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: 15,
    display: "flex",
  },
  container: {
    height: "100%",
    width: "100%",
  },
  flexContainer: {
    display: "flex",
  },
}));

registerPlugin(FilePondPluginFileValidateType);
registerPlugin(FilePondPluginFileValidateSize);
const acceptedFileTypes = ["image/*"];

function OrganizationSettings() {
  const [files, setFiles] = useState([]);
  const [attachmentId, setAttachmentId] = useState(null);
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasModified, setHasModified] = useState(false);
  const apiLocation = useSelector((state) => state.setting.apiLocation);
  const [uploadError, setUploadError] = useState(null);
  const organization = useSelector((state) => {
    if (state.user.currentUser) {
      return state.user.currentUser.organization;
    }
    return {};
  });
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const save = () => {
    setSaving(true);
    taxServiceClient
      .patch(`/organization/${organization.organizationId}`, {
        logoId: attachmentId,
      })
      .then(() => {
        enqueueSnackbar(t("logo_saved_successfully"), {
          variant: "success",
        });
        setSaving(false);
        setHasModified(false);
      })
      .catch(() => {
        enqueueSnackbar(t("error_saving_logo"), {
          variant: "error",
        });
        setSaving(false);
      });
  };

  useEffect(
    () => {
      if (attachmentId) {
        setHasModified(true);
        getAttachmentById(attachmentId);
      } else {
        if (organization.logoId) {
          getAttachmentById(organization.logoId);
        } else {
          setLogo(defaultLogo);
        }
        setHasModified(false);
      }
    },
    [attachmentId]
  );

  const getTransKeyFromHttpStatus = (status, message) =>{
    let translationKey = "unexpected_upload_error";
    switch(status){
      case 409:
        translationKey="virus_detected_in_file"
        break;
      case 413:
        translationKey="file_too_big_to_process"
        break;
      case 418: 
        // I'm a teapot. 
        // Temporary method to handle internal 500 errors. See UV6-1207 
        // Error type is propagated as a translation key
        // First characters are the Http status it prepresents
        // e.g. 500_AV_NOT_DEFINED
        if(!!message && message.length>0){
          translationKey = message;
        }
    }
    return translationKey;
  }

  const getAttachmentById = (attachmentId) => {
    setLogo(spinner);
    attachmentClient
      .get(`/attachment/${attachmentId}/content/`, {
        responseType: "blob",
      })
      .then((res) => {
        let reader = new FileReader();
        reader.readAsDataURL(res.data);
        reader.onloadend = () => {
          setLogo(reader.result);
        };
      })
      .catch((err) => {
        enqueueSnackbar(t("error_retrieving_logo"), {
          variant: "error",
        });
        setLogo(defaultLogo);
        setHasModified(false);
      });
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ marginRight: 10 }}>
          {t("organization_settings_header")}
        </Typography>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{ display: "flex", flexDirection: "column", marginTop: 10 }}
        >
          <Typography variant="h6">{t("organization_logo_header")}</Typography>
          <Typography variant="body2">
            {t("organization_logo_subheader")}
          </Typography>
          <div>
            <img
              style={{
                backgroundPosition: "0px 0px, 10px 10px",
                backgroundSize: "20px 20px",
                backgroundImage:
                  "linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%),linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%)",
                display: "block",
                width: "auto",
                height: "auto",
                maxWidth: 250,
                maxHeight: 250,
                margin: "0 auto 15px auto",
              }}
              src={logo}
              alt="Organization logo"
            />
            {hasModified && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 10,
                }}
              >
                <Typography>{t("save_new_logo")}</Typography>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={save}
                  loading={saving}
                  style={{ marginLeft: 10 }}
                >
                  {t("global_save")}
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={() => setAttachmentId(null)}
                  style={{ marginLeft: 10 }}
                >
                  {t("global_cancel")}
                </ActionButton>
              </div>
            )}
          </div>
          <div style={{ margin: "0 50px" }}>
            <FilePond
              files={files}
              allowMultiple={false}
              allowRevert={true}
              acceptedFileTypes={acceptedFileTypes}
              allowSyncAcceptAttribute={false}
              labelFileTypeNotAllowed={t("invalid_file_type")}
              fileValidateTypeLabelExpectedTypes={t(
                "only_image_types_are_supported"
              )}
              maxFileSize="500KB"
              server={{
                process: (
                  fieldName,
                  file,
                  metadata,
                  load,
                  error,
                  progress,
                  abort,
                  transfer,
                  options
                ) => {
                  let timeout = null;
                  const formData = new FormData();
                  formData.append("file", file, file.name);
                  const request = new XMLHttpRequest();
                  request.withCredentials = true
                  request.open(
                    "POST",
                    `https://${apiLocation}/attachmentservice/attachment/upload/`
                  );
                  request.upload.onprogress = (e) => {
                    progress(e.lengthComputable, e.loaded, e.total);
                  };
                  request.onload = () => {
                    let payload = JSON.parse(request.responseText);
                    if (request.status >= 200 && request.status < 300) {
                      load(payload.id);
                    } else{
                      let key = getTransKeyFromHttpStatus(request.status, payload.message);
                      error(t(key));
                      setUploadError(key);
                    }
                  };
                  request.send(formData);
                  return {
                    abort: () => {
                      request.abort();
                      abort();
                    },
                  };
                },
              }}
              onprocessfile={(_err, file) => {
                setAttachmentId(file.serverId);
              }}
              onupdatefiles={(fileItems) => {
                setFiles(fileItems.map((fileItem) => fileItem.file));
              }}
            />
          </div>
        </div>
      </div>
      <UploadError
        uploadError={uploadError}
        setUploadError={() => setUploadError(null)}
      />
    </div>
  );
}

export default OrganizationSettings;
